import React, { useState, useEffect, useCallback } from 'react';
import { Wrench, LogOut, Calendar, Receipt, Car, History, CreditCard, Loader2, ShieldCheck, Plus, X } from 'lucide-react';
import { portaal } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { OpeningDayPicker } from '../components/OpeningDayPicker';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const fmtDate = (s) => { if (!s) return '—'; const d = new Date(s); return isNaN(d) ? s : d.toLocaleDateString('nl-NL'); };
const fmtDateTime = (s) => { if (!s) return '—'; const d = new Date(s); return isNaN(d) ? s : d.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };

const apkStatus = (verval, t) => {
  if (!verval) return { label: t('Onbekend', 'Onbekend'), c: '#94a3b8' };
  const d = new Date(verval); const days = Math.ceil((d - new Date()) / 86400000);
  if (isNaN(d)) return { label: verval, c: '#94a3b8' };
  if (days < 0) return { label: `${t('Verlopen', 'Verlopen')} (${fmtDate(verval)})`, c: '#dc2626' };
  if (days <= 30) return { label: `${fmtDate(verval)} (${days} ${t('dagen', 'dagen')})`, c: '#d97706' };
  return { label: fmtDate(verval), c: '#16a34a' };
};

export default function KlantPortaalPage({ garageId }) {
  const { t, lang } = useT();
  const [ingelogd, setIngelogd] = useState(portaal.isIngelogd());
  const [profile, setProfile] = useState(null);
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', naam: '', telefoon: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('afspraken');
  const [data, setData] = useState({ afspraken: [], facturen: [], voertuigen: [], historie: [] });
  const [loading, setLoading] = useState(false);
  const [aanvraagOpen, setAanvraagOpen] = useState(false);
  const [opties, setOpties] = useState({ diensten: [], voertuigen: [] });
  const [av, setAv] = useState({ dienstId: '', datum: '', tijd: '', kenteken: '', opmerking: '' });
  const [avBusy, setAvBusy] = useState(false);
  const [avErr, setAvErr] = useState('');
  const [avOk, setAvOk] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [me, afspraken, facturen, voertuigen, historie] = await Promise.all([
        portaal.me(), portaal.afspraken(), portaal.facturen(), portaal.voertuigen(), portaal.historie(),
      ]);
      setProfile(me); setData({ afspraken, facturen, voertuigen, historie });
    } catch (e) {
      if (e.response?.status === 401) { portaal.logout(); setIngelogd(false); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (ingelogd) loadAll(); }, [ingelogd, loadAll]);

  const submit = async () => {
    setErr(''); setBusy(true);
    try {
      const fn = mode === 'register' ? portaal.register : portaal.login;
      const res = await fn(garageId, form);
      portaal.setToken(res.token); setProfile(res); setIngelogd(true);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const uitloggen = () => { portaal.logout(); setIngelogd(false); setProfile(null); setForm({ email: '', password: '', naam: '', telefoon: '' }); };

  const openAanvraag = async () => {
    setAvErr(''); setAvOk(false); setAv({ dienstId: '', datum: '', tijd: '', kenteken: '', opmerking: '' });
    setAanvraagOpen(true);
    try {
      const o = await portaal.afspraakOpties();
      setOpties(o);
      setAv(p => ({ ...p, dienstId: o.diensten?.[0]?.id || '', kenteken: o.voertuigen?.[0]?.kenteken || '' }));
    } catch (e) { /* opties optioneel */ }
  };

  const verstuurAanvraag = async () => {
    setAvErr('');
    if (!av.datum) { setAvErr(t('Kies een gewenste datum', 'Kies een gewenste datum')); return; }
    setAvBusy(true);
    try {
      await portaal.aanvraagAfspraak(av);
      setAvOk(true);
      await loadAll();
    } catch (e) { setAvErr(e.response?.data?.detail || e.message); }
    finally { setAvBusy(false); }
  };
  const today = new Date().toISOString().slice(0, 10);

  // ---- Login / registratie ----
  if (!ingelogd) {
    return (
      <div style={wrap} data-testid="portaal-auth">
        <div style={{ maxWidth: 420, margin: '0 auto', paddingTop: 50 }}>
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <span style={logoBox}><Wrench size={24} color="#fff" /></span>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: '12px 0 2px', color: '#0f172a' }}>{t('Klantportaal', 'Klantportaal')}</h1>
            <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>{t('Bekijk je afspraken, facturen en APK-status', 'Bekijk je afspraken, facturen en APK-status')}</p>
          </div>
          <div style={card}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button data-testid="portaal-tab-login" onClick={() => { setMode('login'); setErr(''); }} style={tabBtn(mode === 'login')}>{t('Inloggen', 'Inloggen')}</button>
              <button data-testid="portaal-tab-register" onClick={() => { setMode('register'); setErr(''); }} style={tabBtn(mode === 'register')}>{t('Account aanmaken', 'Account aanmaken')}</button>
            </div>
            {mode === 'register' && (
              <>
                <Field label={t('Naam', 'Naam')}><input style={inp} data-testid="portaal-naam" value={form.naam} onChange={e => setForm({ ...form, naam: e.target.value })} /></Field>
                <Field label={t('Telefoon (optioneel)', 'Telefoon (optioneel)')}><input style={inp} data-testid="portaal-telefoon" value={form.telefoon} onChange={e => setForm({ ...form, telefoon: e.target.value })} /></Field>
              </>
            )}
            <Field label={t('E-mailadres', 'E-mailadres')}><input style={inp} type="email" data-testid="portaal-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label={t('Wachtwoord', 'Wachtwoord')}><input style={inp} type="password" data-testid="portaal-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} onKeyDown={e => e.key === 'Enter' && submit()} /></Field>
            {err && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }} data-testid="portaal-error">{err}</div>}
            <button style={primaryBtn} data-testid="portaal-submit" onClick={submit} disabled={busy}>
              {busy ? <Loader2 size={16} className="spin" /> : null} {mode === 'register' ? t('Account aanmaken', 'Account aanmaken') : t('Inloggen', 'Inloggen')}
            </button>
          </div>
        </div>
        <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ---- Dashboard ----
  const tabs = [
    { k: 'afspraken', l: t('Afspraken', 'Afspraken'), icon: Calendar },
    { k: 'facturen', l: t('Facturen', 'Facturen'), icon: Receipt },
    { k: 'voertuigen', l: t('Voertuigen', 'Voertuigen'), icon: Car },
    { k: 'historie', l: t('Historie', 'Historie'), icon: History },
  ];

  return (
    <div style={wrap} data-testid="portaal-dashboard">
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 20, color: '#0f172a' }}>{profile?.garageNaam || 'Klantportaal'}</div>
            <div style={{ color: '#64748b', fontSize: 13.5 }}>{t('Welkom', 'Welkom')}, {profile?.naam}</div>
          </div>
          <button style={ghostBtn} data-testid="portaal-logout" onClick={uitloggen}><LogOut size={14} /> {t('Uitloggen', 'Uitloggen')}</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {tabs.map(x => (
            <button key={x.k} data-testid={`portaal-tab-${x.k}`} onClick={() => setTab(x.k)} style={pillBtn(tab === x.k)}>
              <x.icon size={14} /> {x.l}
            </button>
          ))}
        </div>

        {loading ? <div style={{ ...card, textAlign: 'center', color: '#64748b' }}><Loader2 size={18} className="spin" /> {t('Laden…', 'Laden…')}</div> : (
          <div style={card}>
            {tab === 'afspraken' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                  <button style={primaryBtnSm} data-testid="portaal-afspraak-aanvragen-btn" onClick={openAanvraag}>
                    <Plus size={15} /> {t('Afspraak aanvragen', 'Afspraak aanvragen')}
                  </button>
                </div>
                {data.afspraken.length ? data.afspraken.map(a => (
                  <Row key={a.id} testid={`portaal-afspraak-${a.id}`}>
                    <div><div style={{ fontWeight: 600 }}>{a.dienst}</div><div style={sub}>{fmtDateTime(a.start)}{a.kenteken ? ` · ${a.kenteken}` : ''}</div></div>
                    <span style={badge(a.status === 'Bevestigd' ? '#16a34a' : '#d97706')}>{a.status}</span>
                  </Row>
                )) : <Empty>{t('Nog geen afspraken', 'Nog geen afspraken')}</Empty>}
              </>
            )}
            {tab === 'facturen' && (
              data.facturen.length ? data.facturen.map(f => (
                <Row key={f.id} testid={`portaal-factuur-${f.id}`}>
                  <div><div style={{ fontWeight: 600 }}>{f.nummer}</div><div style={sub}>{fmtDate(f.datum)} · {t('open', 'open')}: {eur(f.openstaand)}</div></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 700 }}>{eur(f.totaal)}</span>
                    {f.openstaand > 0 && f.betaalLink
                      ? <a href={f.betaalLink} style={payBtn} data-testid={`portaal-betaal-${f.id}`}><CreditCard size={13} /> {t('Betaal', 'Betaal')}</a>
                      : <span style={badge(f.openstaand <= 0 ? '#16a34a' : '#94a3b8')}>{f.openstaand <= 0 ? t('Betaald', 'Betaald') : f.status}</span>}
                  </div>
                </Row>
              )) : <Empty>{t('Nog geen facturen', 'Nog geen facturen')}</Empty>
            )}
            {tab === 'voertuigen' && (
              data.voertuigen.length ? data.voertuigen.map((v, i) => {
                const apk = apkStatus(v.apkVervalt, t);
                return (
                  <Row key={i} testid={`portaal-voertuig-${v.kenteken}`}>
                    <div><div style={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>{v.kenteken}</div><div style={sub}>{[v.merk, v.model].filter(Boolean).join(' ')}</div></div>
                    <div style={{ textAlign: 'right', fontSize: 13 }}><div style={{ color: '#64748b', fontSize: 11 }}><ShieldCheck size={11} style={{ verticalAlign: -1 }} /> APK</div><span style={{ color: apk.c, fontWeight: 600 }}>{apk.label}</span></div>
                  </Row>
                );
              }) : <Empty>{t('Geen voertuigen gekoppeld', 'Geen voertuigen gekoppeld')}</Empty>
            )}
            {tab === 'historie' && (
              data.historie.length ? data.historie.map(h => (
                <Row key={h.id} testid={`portaal-historie-${h.id}`}>
                  <div><div style={{ fontWeight: 600 }}>{h.nummer} · {h.type}</div><div style={sub}>{fmtDate(h.datum)}{h.kenteken ? ` · ${h.kenteken}` : ''}{h.omschrijving ? ` — ${h.omschrijving}` : ''}</div></div>
                  <span style={{ fontWeight: 600 }}>{eur(h.totaal)}</span>
                </Row>
              )) : <Empty>{t('Nog geen servicehistorie', 'Nog geen servicehistorie')}</Empty>
            )}
          </div>
        )}
      </div>
      {aanvraagOpen && (
        <div style={overlay} data-testid="portaal-aanvraag-modal" onClick={() => !avBusy && setAanvraagOpen(false)}>
          <div style={{ ...card, maxWidth: 460, width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>{t('Afspraak aanvragen', 'Afspraak aanvragen')}</div>
              <button style={iconBtn} data-testid="portaal-aanvraag-close" onClick={() => setAanvraagOpen(false)}><X size={16} /></button>
            </div>
            {avOk ? (
              <div data-testid="portaal-aanvraag-success" style={{ textAlign: 'center', padding: '14px 0' }}>
                <div style={{ color: '#16a34a', fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{t('Aanvraag verzonden!', 'Aanvraag verzonden!')}</div>
                <p style={{ color: '#64748b', fontSize: 13.5, margin: '0 0 16px' }}>{t('De garage neemt contact op om je afspraak te bevestigen.', 'De garage neemt contact op om je afspraak te bevestigen.')}</p>
                <button style={primaryBtn} data-testid="portaal-aanvraag-done" onClick={() => setAanvraagOpen(false)}>{t('Sluiten', 'Sluiten')}</button>
              </div>
            ) : (
              <>
                <Field label={t('Dienst', 'Dienst')}>
                  <select style={inp} data-testid="portaal-aanvraag-dienst" value={av.dienstId} onChange={e => setAv({ ...av, dienstId: e.target.value })}>
                    {(opties.diensten || []).map(d => <option key={d.id} value={d.id}>{d.naam}</option>)}
                  </select>
                </Field>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <Field label={t('Gewenste datum', 'Gewenste datum')}>
                      <OpeningDayPicker
                        openingstijden={opties.openingstijden || {}}
                        value={av.datum}
                        onChange={(d) => setAv({ ...av, datum: d })}
                        maxDays={90}
                        lang={lang}
                        testid="portaal-aanvraag-datum"
                        placeholder={t('Kies een datum', 'Kies een datum')}
                      />
                    </Field>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Field label={t('Tijd (optioneel)', 'Tijd (optioneel)')}><input style={inp} type="time" data-testid="portaal-aanvraag-tijd" value={av.tijd} onChange={e => setAv({ ...av, tijd: e.target.value })} /></Field>
                  </div>
                </div>
                {opties.voertuigen?.length ? (
                  <Field label={t('Voertuig', 'Voertuig')}>
                    <select style={inp} data-testid="portaal-aanvraag-voertuig" value={av.kenteken} onChange={e => setAv({ ...av, kenteken: e.target.value })}>
                      {opties.voertuigen.map(v => <option key={v.kenteken} value={v.kenteken}>{[v.kenteken, v.merk, v.model].filter(Boolean).join(' · ')}</option>)}
                    </select>
                  </Field>
                ) : null}
                <Field label={t('Opmerking', 'Opmerking')}><textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} data-testid="portaal-aanvraag-opmerking" value={av.opmerking} onChange={e => setAv({ ...av, opmerking: e.target.value })} placeholder={t('Beschrijf kort wat er moet gebeuren…', 'Beschrijf kort wat er moet gebeuren…')} /></Field>
                {avErr && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }} data-testid="portaal-aanvraag-error">{avErr}</div>}
                <button style={primaryBtn} data-testid="portaal-aanvraag-submit" onClick={verstuurAanvraag} disabled={avBusy}>
                  {avBusy ? <Loader2 size={16} className="spin" /> : null} {t('Aanvraag versturen', 'Aanvraag versturen')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => <div style={{ marginBottom: 12 }}><label style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5 }}>{label}</label>{children}</div>;
const Row = ({ children, testid }) => <div data-testid={testid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 4px', borderBottom: '1px solid #eef2f7', gap: 12 }}>{children}</div>;
const Empty = ({ children }) => <div style={{ textAlign: 'center', color: '#94a3b8', padding: '28px 0', fontSize: 14 }}>{children}</div>;

const wrap = { minHeight: '100vh', background: '#f1f5f9', fontFamily: 'system-ui, sans-serif', padding: '24px 14px' };
const card = { background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 4px 24px rgba(15,23,42,0.06)' };
const logoBox = { width: 48, height: 48, borderRadius: 12, background: '#2563eb', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 9, fontSize: 14, boxSizing: 'border-box' };
const sub = { color: '#64748b', fontSize: 12.5 };
const primaryBtn = { width: '100%', padding: '12px', border: 'none', borderRadius: 10, background: '#2563eb', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 };
const primaryBtnSm = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', borderRadius: 9, background: '#2563eb', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 };
const iconBtn = { background: '#f1f5f9', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#475569' };
const ghostBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e2e8f0', color: '#475569', padding: '8px 14px', borderRadius: 9, fontWeight: 600, cursor: 'pointer', fontSize: 13.5 };
const tabBtn = (active) => ({ flex: 1, padding: '9px', border: 'none', borderRadius: 8, background: active ? '#0f172a' : '#f1f5f9', color: active ? '#fff' : '#475569', fontWeight: 600, cursor: 'pointer', fontSize: 14 });
const pillBtn = (active) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid', borderColor: active ? '#2563eb' : '#e2e8f0', background: active ? '#2563eb' : '#fff', color: active ? '#fff' : '#475569', borderRadius: 999, fontWeight: 600, cursor: 'pointer', fontSize: 13.5 });
const payBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, background: '#2563eb', color: '#fff', padding: '6px 12px', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none' };
const badge = (c) => ({ background: c + '1a', color: c, padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 });
